import { useState, useEffect } from "react";
import { http, API, resolveImage } from "@/lib/api";
import Layout from "@/components/Layout";
import { useParams, useSearchParams, Link } from "react-router-dom";
import { toast } from "sonner";
import { motion } from "framer-motion";
import { Calendar, Clock, MapPin, Users, Check, Loader2, CreditCard, AlertTriangle, Share2, ChevronDown, ExternalLink } from "lucide-react";

export default function WorkshopDetail() {
  const { slug } = useParams();
  const [searchParams] = useSearchParams();
  const [workshop, setWorkshop] = useState(null);
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const regId = searchParams.get("registration");
  const regStatus = searchParams.get("status");

  useEffect(() => {
    Promise.all([
      http.get(`/workshops/public/workshops/${slug}`),
      http.get("/workshops/public/settings"),
    ]).then(([w, s]) => { setWorkshop(w.data); setSettings(s.data); })
      .catch(() => toast.error("Workshop not found"))
      .finally(() => setLoading(false));
  }, [slug]);

  // Confirm Stripe payment on return
  useEffect(() => {
    if (regId && regStatus === "success") {
      http.post("/workshops/public/confirm-stripe", { registration_id: regId }).catch(() => {});
    }
  }, [regId, regStatus]);

  if (loading || !workshop) return <Layout><div className="min-h-screen bg-[#0A0A0A] flex items-center justify-center"><Loader2 className="text-[#D4AF37] animate-spin" size={32} /></div></Layout>;

  const isOpen = ["published", "registration_open", "almost_full"].includes(workshop.status);
  const isWaitlist = workshop.status === "waitlist_open" || workshop.status === "sold_out";

  if (regId) return <Layout><ConfirmationView regId={regId} status={regStatus} workshop={workshop} /></Layout>;

  return (
    <Layout>
      <div className="min-h-screen bg-[#0A0A0A]">
        {/* JSON-LD Structured Event Data */}
        <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify({
          "@context": "https://schema.org", "@type": "Event",
          "name": workshop.title, "description": workshop.description || workshop.full_description,
          "startDate": `${workshop.date}T${workshop.start_time?.replace(/\s/g, "") || ""}`,
          "endDate": `${workshop.date}T${workshop.end_time?.replace(/\s/g, "") || ""}`,
          "eventStatus": "https://schema.org/EventScheduled",
          "eventAttendanceMode": "https://schema.org/OfflineEventAttendanceMode",
          "location": { "@type": "Place", "name": workshop.location_name, "address": { "@type": "PostalAddress", "streetAddress": workshop.address } },
          "offers": { "@type": "Offer", "price": (workshop.price / 100).toFixed(2), "priceCurrency": "USD", "availability": workshop.seats_remaining > 0 ? "https://schema.org/InStock" : "https://schema.org/SoldOut", "url": window.location.href },
          "organizer": { "@type": "Organization", "name": "Mirror Conversations\u2122", "url": "https://simpletruthsclub.com" },
          "performer": { "@type": "Person", "name": workshop.facilitator || "Mickey Trivett" },
          "image": workshop.featured_image || "",
        })}} />

        {/* Hero */}
        <section className="relative py-24 md:py-32 px-6" data-testid="ws-detail-hero">
          {workshop.featured_image && <div className="absolute inset-0 bg-cover bg-center opacity-15" style={{ backgroundImage: `url(${resolveImage(workshop.featured_image)})` }} />}
          <div className="absolute inset-0 bg-gradient-to-b from-transparent via-[#0A0A0A]/70 to-[#0A0A0A]" />
          <div className="relative z-10 max-w-3xl mx-auto text-center">
            <p className="font-ui text-[10px] tracking-[0.3em] text-[#D4AF37] uppercase mb-4">Mirror Conversations™ Workshop</p>
            <h1 className="font-display text-4xl md:text-5xl text-[#F5F5F0] mb-3">{workshop.title}</h1>
            {workshop.subtitle && <p className="font-body text-base text-zinc-400 mb-8">{workshop.subtitle}</p>}
            <div className="flex flex-wrap gap-4 justify-center text-sm text-zinc-400">
              <span className="flex items-center gap-2"><Calendar size={14} className="text-[#D4AF37]" /> {workshop.day_of_week}, {workshop.date}</span>
              <span className="flex items-center gap-2"><Clock size={14} className="text-[#D4AF37]" /> {workshop.start_time} – {workshop.end_time}</span>
              <span className="flex items-center gap-2"><MapPin size={14} className="text-[#D4AF37]" /> {workshop.location_name}</span>
            </div>
          </div>
        </section>

        <div className="max-w-5xl mx-auto px-6 pb-20">
          <div className="grid lg:grid-cols-[1fr_340px] gap-10">
            {/* Main Content */}
            <div className="space-y-10">
              {workshop.full_description && (
                <div><h2 className="font-display text-xl text-[#F5F5F0] mb-3">About This Workshop</h2><p className="font-body text-sm text-zinc-400 leading-relaxed whitespace-pre-line">{workshop.full_description}</p></div>
              )}
              {workshop.what_to_expect && (
                <div><h2 className="font-display text-xl text-[#F5F5F0] mb-3">What to Expect</h2><p className="font-body text-sm text-zinc-400 leading-relaxed whitespace-pre-line">{workshop.what_to_expect}</p></div>
              )}
              {workshop.included?.length > 0 && (
                <div><h2 className="font-display text-xl text-[#F5F5F0] mb-3">What&apos;s Included</h2>
                  <ul className="space-y-2">{workshop.included.map((item, i) => (
                    <li key={i} className="flex items-start gap-3"><Check size={14} className="text-[#D4AF37] mt-0.5 shrink-0" /><span className="font-body text-sm text-zinc-300">{item}</span></li>
                  ))}</ul>
                </div>
              )}
              {workshop.who_is_for && <div><h2 className="font-display text-xl text-[#F5F5F0] mb-3">Who Is This For?</h2><p className="font-body text-sm text-zinc-400 leading-relaxed">{workshop.who_is_for}</p></div>}
              {workshop.what_to_bring && <div><h2 className="font-display text-xl text-[#F5F5F0] mb-3">What to Bring</h2><p className="font-body text-sm text-zinc-400">{workshop.what_to_bring}</p></div>}
              {workshop.facilitator && <div><h2 className="font-display text-xl text-[#F5F5F0] mb-3">Your Facilitator</h2><p className="font-body text-sm text-zinc-400">{workshop.facilitator}</p></div>}
              {workshop.cancellation_policy && <div><h2 className="font-display text-xl text-[#F5F5F0] mb-3">Cancellation Policy</h2><p className="font-body text-sm text-zinc-500">{workshop.cancellation_policy}</p></div>}
            </div>

            {/* Sidebar */}
            <div className="lg:sticky lg:top-24 h-fit space-y-4">
              <div className="rounded-2xl border border-white/[0.06] bg-white/[0.02] p-6 space-y-4">
                <div className="text-center"><span className="font-display text-4xl text-[#D4AF37]">{workshop.display_price}</span><span className="font-body text-sm text-zinc-500"> / person</span></div>
                <div className="space-y-2 text-sm">
                  <p className="font-body text-zinc-400 flex justify-between"><span>Date</span> <span className="text-[#F5F5F0]">{workshop.date}</span></p>
                  <p className="font-body text-zinc-400 flex justify-between"><span>Time</span> <span className="text-[#F5F5F0]">{workshop.start_time} – {workshop.end_time}</span></p>
                  <p className="font-body text-zinc-400 flex justify-between"><span>Duration</span> <span className="text-[#F5F5F0]">{workshop.duration}</span></p>
                  <p className="font-body text-zinc-400 flex justify-between"><span>Capacity</span> <span className="text-[#F5F5F0]">{workshop.capacity} people</span></p>
                </div>
                <div className="text-center">
                  {workshop.seats_remaining > 0 && workshop.seats_remaining <= 3 && (
                    <p className="font-ui text-xs text-amber-400 tracking-wider uppercase">Only {workshop.seats_remaining} Seat{workshop.seats_remaining !== 1 ? "s" : ""} Left</p>
                  )}
                  {workshop.seats_remaining > 3 && <p className="font-body text-xs text-zinc-500">{workshop.seats_remaining} seats available</p>}
                  {workshop.seats_remaining <= 0 && <p className="font-ui text-xs text-red-400 tracking-wider uppercase">Sold Out</p>}
                </div>
                {isOpen && <a href="#register" className="block w-full bg-[#D4AF37] text-[#0A0A0A] py-3.5 rounded-full font-ui tracking-wide uppercase text-sm text-center hover:shadow-[0_0_20px_rgba(212,175,55,0.3)] transition-all" data-testid="ws-claim-btn">{workshop.btn_text}</a>}
                {isWaitlist && <a href="#waitlist" className="block w-full border border-[#D4AF37] text-[#D4AF37] py-3.5 rounded-full font-ui tracking-wide uppercase text-sm text-center hover:bg-[#D4AF37] hover:text-[#0A0A0A] transition-all">Join the Waitlist</a>}
              </div>
              {workshop.address && (
                <a href={`https://maps.google.com/?q=${encodeURIComponent(workshop.address)}`} target="_blank" rel="noreferrer"
                  className="rounded-xl border border-white/[0.06] bg-white/[0.02] p-4 flex items-center gap-3 hover:border-zinc-600 transition-all">
                  <MapPin size={16} className="text-[#D4AF37] shrink-0" />
                  <div><p className="font-body text-xs text-[#F5F5F0]">{workshop.location_name}</p><p className="font-body text-[10px] text-zinc-500">{workshop.address}</p></div>
                  <ExternalLink size={12} className="text-zinc-600 shrink-0 ml-auto" />
                </a>
              )}
            </div>
          </div>

          {/* Registration Form */}
          {isOpen && <div id="register" className="mt-16"><RegistrationForm workshop={workshop} settings={settings} /></div>}
          {isWaitlist && <div id="waitlist" className="mt-16"><WaitlistForm workshop={workshop} /></div>}
        </div>
      </div>
    </Layout>
  );
}

/* ═══ REGISTRATION FORM ═══ */
function RegistrationForm({ workshop, settings }) {
  const [step, setStep] = useState(1); // 1=details, 2=payment
  const [seats, setSeats] = useState(1);
  const [form, setForm] = useState({ full_name: "", email: "", phone: "", additional_attendees: [], emergency_contact: "", accessibility_request: "", how_heard: "", agreement: false, typed_signature: "" });
  const [payMethod, setPayMethod] = useState("stripe");
  const [reservationId, setReservationId] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const u = (k, v) => setForm(p => ({ ...p, [k]: v }));
  const pm = settings.payment_methods || {};
  const maxSeats = Math.min(workshop.seats_remaining, 6);
  const inp = "w-full bg-transparent border border-white/10 rounded-lg px-3 py-2.5 text-sm text-[#F5F5F0] font-body focus:border-[#D4AF37] focus:outline-none transition-colors";
  const lbl = "font-body text-xs tracking-[0.12em] uppercase text-zinc-400 block mb-1.5";

  const reserve = async () => {
    if (!form.full_name.trim() || !form.email.trim()) { toast.error("Name and email are required"); return; }
    if (!form.agreement) { toast.error("You must agree to the terms"); return; }
    if (!form.typed_signature.trim()) { toast.error("Typed signature is required"); return; }
    setSubmitting(true);
    try {
      const { data } = await http.post("/workshops/public/reserve-seats", { workshop_id: workshop.id, seats });
      setReservationId(data.reservation_id);
      setStep(2);
    } catch (e) { toast.error(e.response?.data?.detail || "Could not reserve seats"); }
    finally { setSubmitting(false); }
  };

  const pay = async () => {
    setSubmitting(true);
    try {
      const { data } = await http.post("/workshops/public/register", {
        reservation_id: reservationId, ...form, seats, payment_method: payMethod,
        additional_attendees: form.additional_attendees.filter(Boolean),
      });
      if (data.checkout_url) {
        window.location.href = data.checkout_url;
      } else {
        // Venmo/CashApp — redirect to confirmation
        window.location.href = `/workshops/${workshop.slug}?registration=${data.registration_id}&status=pending`;
      }
    } catch (e) { toast.error(e.response?.data?.detail || "Registration failed"); }
    finally { setSubmitting(false); }
  };

  return (
    <div className="max-w-2xl mx-auto" data-testid="ws-registration">
      <h2 className="font-display text-2xl text-[#F5F5F0] text-center mb-8">{workshop.btn_text || "Claim Your Seat"}</h2>

      {step === 1 && (
        <div className="space-y-5 rounded-2xl border border-white/[0.06] bg-white/[0.02] p-6 md:p-8">
          {/* Seats */}
          <div><label className={lbl}>Number of Seats</label>
            <div className="flex gap-2">{Array.from({ length: maxSeats }, (_, i) => i + 1).map(n => (
              <button key={n} onClick={() => { setSeats(n); const att = [...form.additional_attendees]; while(att.length < n-1) att.push(""); while(att.length > n-1) att.pop(); u("additional_attendees", att); }}
                className={`w-10 h-10 rounded-full font-body text-sm ${seats === n ? "bg-[#D4AF37] text-[#0A0A0A]" : "border border-white/10 text-zinc-400"}`}>{n}</button>
            ))}</div>
            <p className="font-body text-xs text-zinc-500 mt-1">Total: {workshop.display_price} × {seats} = ${(workshop.price * seats / 100).toFixed(2)}</p>
          </div>

          <div className="grid sm:grid-cols-2 gap-4">
            <div><label className={lbl}>Full Name *</label><input required value={form.full_name} onChange={e => u("full_name", e.target.value)} className={inp} data-testid="ws-reg-name" /></div>
            <div><label className={lbl}>Email *</label><input required type="email" value={form.email} onChange={e => u("email", e.target.value)} className={inp} data-testid="ws-reg-email" /></div>
          </div>
          <div><label className={lbl}>Phone</label><input value={form.phone} onChange={e => u("phone", e.target.value)} className={inp} /></div>

          {seats > 1 && (
            <div><label className={lbl}>Additional Attendee Names</label>
              {form.additional_attendees.map((att, i) => (
                <input key={i} value={att} onChange={e => { const a = [...form.additional_attendees]; a[i] = e.target.value; u("additional_attendees", a); }}
                  placeholder={`Attendee ${i + 2}`} className={`${inp} mb-2`} />
              ))}
            </div>
          )}

          <div><label className={lbl}>Emergency Contact (optional)</label><input value={form.emergency_contact} onChange={e => u("emergency_contact", e.target.value)} className={inp} /></div>
          <div><label className={lbl}>Accessibility or Accommodation Request (optional)</label><input value={form.accessibility_request} onChange={e => u("accessibility_request", e.target.value)} className={inp} /></div>
          <div><label className={lbl}>How did you hear about this? (optional)</label><input value={form.how_heard} onChange={e => u("how_heard", e.target.value)} className={inp} /></div>

          {/* Agreement */}
          <div className="rounded-xl border border-white/[0.06] bg-white/[0.01] p-4 space-y-3">
            <label className="flex items-start gap-3 cursor-pointer">
              <input type="checkbox" checked={form.agreement} onChange={e => u("agreement", e.target.checked)} className="accent-[#D4AF37] mt-1" data-testid="ws-reg-agree" />
              <span className="font-body text-sm text-zinc-300">I agree to the workshop terms, cancellation policy, and understand this is an educational experience, not therapy or medical treatment.</span>
            </label>
            <div><label className={lbl}>Type Your Full Name *</label>
              <input value={form.typed_signature} onChange={e => u("typed_signature", e.target.value)}
                className="w-full bg-transparent border-b border-white/10 text-[#F5F5F0] py-2 focus:border-[#D4AF37] focus:outline-none text-lg"
                style={{ fontFamily: "'Cormorant Garamond', serif", fontStyle: "italic" }} data-testid="ws-reg-sig" />
            </div>
          </div>

          <button onClick={reserve} disabled={submitting} data-testid="ws-reg-continue"
            className="w-full bg-[#D4AF37] text-[#0A0A0A] py-4 rounded-full font-ui tracking-wide uppercase text-sm flex items-center justify-center gap-2 hover:shadow-[0_0_20px_rgba(212,175,55,0.3)] transition-all disabled:opacity-50">
            {submitting ? <Loader2 size={16} className="animate-spin" /> : <CreditCard size={16} />}
            {submitting ? "Reserving..." : "Continue to Payment"}
          </button>
        </div>
      )}

      {step === 2 && (
        <div className="space-y-5 rounded-2xl border border-white/[0.06] bg-white/[0.02] p-6 md:p-8" data-testid="ws-payment">
          <p className="font-body text-sm text-zinc-400 text-center">Your {seats} seat{seats > 1 ? "s are" : " is"} reserved for 15 minutes. Choose a payment method.</p>

          <div className="space-y-3">
            {pm.stripe && (
              <button onClick={() => setPayMethod("stripe")} className={`w-full rounded-xl border p-4 text-left transition-all ${payMethod === "stripe" ? "border-[#D4AF37] bg-[#D4AF37]/[0.05]" : "border-white/[0.06]"}`}>
                <p className="font-body text-sm text-[#F5F5F0]">Pay with Card (Stripe)</p>
                <p className="font-body text-xs text-zinc-500">Credit or debit card — instant confirmation</p>
              </button>
            )}
            {pm.venmo && (
              <button onClick={() => setPayMethod("venmo")} className={`w-full rounded-xl border p-4 text-left transition-all ${payMethod === "venmo" ? "border-[#D4AF37] bg-[#D4AF37]/[0.05]" : "border-white/[0.06]"}`}>
                <p className="font-body text-sm text-[#F5F5F0]">Venmo</p>
                <p className="font-body text-xs text-zinc-500">Send to {settings?.venmo_handle || "@MickeyTrivett"} — manual verification</p>
              </button>
            )}
            {pm.cashapp && (
              <button onClick={() => setPayMethod("cashapp")} className={`w-full rounded-xl border p-4 text-left transition-all ${payMethod === "cashapp" ? "border-[#D4AF37] bg-[#D4AF37]/[0.05]" : "border-white/[0.06]"}`}>
                <p className="font-body text-sm text-[#F5F5F0]">Cash App</p>
                <p className="font-body text-xs text-zinc-500">Send to {settings?.cashapp_handle || "$MickeyTrivett"} — manual verification</p>
              </button>
            )}
          </div>

          <div className="font-body text-sm text-center">
            <p className="text-zinc-400">Total: <span className="text-[#D4AF37] text-lg font-display">${(workshop.price * seats / 100).toFixed(2)}</span></p>
          </div>

          <button onClick={pay} disabled={submitting} data-testid="ws-pay-btn"
            className="w-full bg-[#D4AF37] text-[#0A0A0A] py-4 rounded-full font-ui tracking-wide uppercase text-sm flex items-center justify-center gap-2 hover:shadow-[0_0_20px_rgba(212,175,55,0.3)] transition-all disabled:opacity-50">
            {submitting ? <Loader2 size={16} className="animate-spin" /> : null}
            {submitting ? "Processing..." : payMethod === "stripe" ? "Pay Now" : "Submit Registration"}
          </button>
          <button onClick={() => setStep(1)} className="w-full font-body text-xs text-zinc-600 hover:text-zinc-400 text-center">Back to details</button>
        </div>
      )}
    </div>
  );
}

/* ═══ CONFIRMATION ═══ */
function ConfirmationView({ regId, status, workshop }) {
  const [reg, setReg] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    http.get(`/workshops/public/registration/${regId}`)
      .then(r => setReg(r.data))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [regId]);

  if (loading) return <div className="min-h-screen bg-[#0A0A0A] flex items-center justify-center"><Loader2 className="text-[#D4AF37] animate-spin" size={32} /></div>;

  const isPending = reg?.payment_status === "pending_verification";
  const isConfirmed = reg?.payment_status === "paid" || status === "success";

  // Convert workshop time strings (e.g. "5:30 PM") to Google Calendar format (HHMMSS)
  const timeToGcal = (timeStr) => {
    if (!timeStr) return "000000";
    const match = timeStr.match(/(\d{1,2}):(\d{2})\s*(AM|PM)/i);
    if (!match) return "000000";
    let h = parseInt(match[1], 10);
    const m = match[2];
    const ampm = match[3].toUpperCase();
    if (ampm === "PM" && h !== 12) h += 12;
    if (ampm === "AM" && h === 12) h = 0;
    return `${String(h).padStart(2, "0")}${m}00`;
  };
  const dateStr = workshop.date?.replace(/-/g, "") || "";
  const calendarUrl = `https://calendar.google.com/calendar/render?action=TEMPLATE&text=${encodeURIComponent(workshop.title)}&dates=${dateStr}T${timeToGcal(workshop.start_time)}/${dateStr}T${timeToGcal(workshop.end_time)}&location=${encodeURIComponent(workshop.address || "")}&details=${encodeURIComponent(workshop.description || "")}`;

  return (
    <div className="min-h-screen bg-[#0A0A0A] py-20 px-6" data-testid="ws-confirmation">
      <div className="max-w-xl mx-auto text-center">
        <Check size={48} className={`mx-auto mb-6 ${isConfirmed ? "text-[#D4AF37]" : "text-amber-400"}`} />
        <h1 className="font-display text-3xl text-[#F5F5F0] mb-4">{isConfirmed ? "You're In." : "Almost There."}</h1>

        {isPending && (
          <div className="rounded-xl border border-amber-500/20 bg-amber-500/[0.03] p-5 mb-8 text-left">
            <p className="font-body text-sm text-amber-300 mb-2">Your seat request has been received and is awaiting payment verification.</p>
            <p className="font-body text-xs text-zinc-400">Your registration is not final until it is approved. Please send {workshop.display_price} × {reg?.seats || 1} to {reg?.payment_method === "venmo" ? "@MickeyTrivett on Venmo" : "$MickeyTrivett on Cash App"}.</p>
          </div>
        )}

        {reg && (
          <div className="rounded-2xl border border-white/[0.06] bg-white/[0.02] p-6 text-left space-y-3 mb-8">
            <p className="font-body text-xs text-zinc-400 flex justify-between"><span>Confirmation</span> <span className="text-[#D4AF37] font-mono">{reg.confirmation_number}</span></p>
            <p className="font-body text-xs text-zinc-400 flex justify-between"><span>Workshop</span> <span className="text-[#F5F5F0]">{reg.workshop_title}</span></p>
            <p className="font-body text-xs text-zinc-400 flex justify-between"><span>Name</span> <span className="text-[#F5F5F0]">{reg.full_name}</span></p>
            <p className="font-body text-xs text-zinc-400 flex justify-between"><span>Seats</span> <span className="text-[#F5F5F0]">{reg.seats}</span></p>
            <p className="font-body text-xs text-zinc-400 flex justify-between"><span>Amount</span> <span className="text-[#F5F5F0]">${((reg.amount || 0) / 100).toFixed(2)}</span></p>
            <p className="font-body text-xs text-zinc-400 flex justify-between"><span>Payment</span> <span className={`uppercase text-[10px] ${isConfirmed ? "text-green-400" : "text-amber-400"}`}>{isConfirmed ? "Paid" : reg.payment_status?.replace(/_/g, " ")}</span></p>
            <p className="font-body text-xs text-zinc-400 flex justify-between"><span>Date</span> <span className="text-[#F5F5F0]">{workshop.date} · {workshop.start_time}</span></p>
            <p className="font-body text-xs text-zinc-400 flex justify-between"><span>Location</span> <span className="text-[#F5F5F0]">{workshop.location_name}</span></p>
          </div>
        )}

        <div className="flex flex-col sm:flex-row gap-3 justify-center">
          <a href={calendarUrl} target="_blank" rel="noreferrer" className="border border-[#D4AF37] text-[#D4AF37] px-5 py-2.5 rounded-full font-ui text-xs tracking-wide uppercase hover:bg-[#D4AF37] hover:text-[#0A0A0A] transition-all flex items-center justify-center gap-2"><Calendar size={14} /> Add to Calendar</a>
          {workshop.address && <a href={`https://maps.google.com/?q=${encodeURIComponent(workshop.address)}`} target="_blank" rel="noreferrer" className="border border-white/10 text-zinc-400 px-5 py-2.5 rounded-full font-ui text-xs tracking-wide hover:border-zinc-600 transition-all flex items-center justify-center gap-2"><MapPin size={14} /> Directions</a>}
          <Link to="/workshops" className="border border-white/10 text-zinc-400 px-5 py-2.5 rounded-full font-ui text-xs tracking-wide hover:border-zinc-600 transition-all">Back to Workshops</Link>
        </div>
      </div>
    </div>
  );
}

/* ═══ WAITLIST ═══ */
function WaitlistForm({ workshop }) {
  const [form, setForm] = useState({ name: "", email: "", phone: "", seats_requested: 1 });
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const u = (k, v) => setForm(p => ({ ...p, [k]: v }));
  const inp = "w-full bg-transparent border border-white/10 rounded-lg px-3 py-2.5 text-sm text-[#F5F5F0] font-body focus:border-[#D4AF37] focus:outline-none";

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await http.post("/workshops/public/waitlist", { workshop_id: workshop.id, ...form });
      setDone(true);
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
    finally { setSubmitting(false); }
  };

  if (done) return (
    <div className="max-w-md mx-auto text-center" data-testid="ws-waitlist-done">
      <Check size={32} className="text-[#D4AF37] mx-auto mb-4" />
      <h3 className="font-display text-xl text-[#F5F5F0] mb-2">You&apos;re on the list.</h3>
      <p className="font-body text-sm text-zinc-400">We&apos;ll contact you if a seat opens up.</p>
    </div>
  );

  return (
    <div className="max-w-md mx-auto" data-testid="ws-waitlist">
      <h2 className="font-display text-2xl text-[#F5F5F0] text-center mb-6">Join the Waitlist</h2>
      <form onSubmit={submit} className="space-y-4 rounded-2xl border border-white/[0.06] bg-white/[0.02] p-6">
        <input required value={form.name} onChange={e => u("name", e.target.value)} placeholder="Full Name *" className={inp} data-testid="ws-wl-name" />
        <input required type="email" value={form.email} onChange={e => u("email", e.target.value)} placeholder="Email *" className={inp} data-testid="ws-wl-email" />
        <input value={form.phone} onChange={e => u("phone", e.target.value)} placeholder="Phone" className={inp} />
        <button type="submit" disabled={submitting} className="w-full bg-[#D4AF37] text-[#0A0A0A] py-3.5 rounded-full font-ui tracking-wide uppercase text-sm disabled:opacity-50" data-testid="ws-wl-submit">{submitting ? "Joining..." : "Join the Waitlist"}</button>
      </form>
    </div>
  );
}
